/**
 * FloorPlan — loads floor plan SVG, can show as a corner minimap,
 * and provides parsed line data for 3D overlay rendering.
 */

export interface FloorPlanLine {
  /** Points in the mesh's original 2D cross-section coordinate space. */
  points: Array<{ x: number; y: number }>;
}

export type FloorPlanClickMode = "teleport" | "align";

export class FloorPlan {
  private container: HTMLElement;
  private svgEl: SVGElement | null = null;
  private onTeleport?: (normX: number, normZ: number) => void;
  private currentRotation = 0;
  private indicatorSvg: SVGSVGElement;
  private indicatorPoly: SVGPolygonElement;

  /** Parsed line data in mesh-space 2D coordinates. */
  lines: FloorPlanLine[] = [];

  /** Dominant wall angle in radians (from SVG metadata). */
  rotation = 0;

  /** Routes clicks to either the teleport callback or an alignment callback. */
  clickMode: FloorPlanClickMode = "teleport";
  onAlignPick?: (planX: number, planZ: number) => void;

  // ── Stored SVG metadata (set during load, used for coord conversion) ──
  private scale = 100;
  private padding = 0.5;
  private minX = 0;
  private minY = 0;
  private svgWidthPx = 0;
  private svgHeightPx = 0;

  /** True when the SVG has been loaded and is ready to display. */
  get loaded(): boolean {
    return this.svgEl !== null;
  }

  constructor(viewerContainer: HTMLElement) {
    this.container = document.createElement("div");
    this.container.style.cssText = `
      position: absolute;
      bottom: 16px; right: 16px;
      width: 200px; height: 200px;
      border: 1px solid rgba(74,158,255,0.4);
      border-radius: 8px;
      overflow: hidden;
      cursor: crosshair;
      display: none;
      background: #0f0f0f;
      align-items: center; justify-content: center;
    `;
    viewerContainer.appendChild(this.container);

    // Camera indicator overlay — rotates together with the floor plan SVG
    this.indicatorSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    this.indicatorSvg.setAttribute("viewBox", "0 0 200 200");
    this.indicatorSvg.style.cssText =
      "position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;";
    this.indicatorPoly = document.createElementNS("http://www.w3.org/2000/svg", "polygon");
    this.indicatorPoly.setAttribute("points", "0,-12 -7,7 7,7");
    this.indicatorPoly.setAttribute("fill", "#4a9eff");
    this.indicatorPoly.setAttribute("stroke", "white");
    this.indicatorPoly.setAttribute("stroke-width", "1.5");
    this.indicatorPoly.setAttribute("stroke-linejoin", "round");
    this.indicatorPoly.setAttribute("opacity", "0.9");
    this.indicatorSvg.appendChild(this.indicatorPoly);
    this.container.appendChild(this.indicatorSvg);
  }

  async load(svgUrl: string, onTeleport: (nx: number, nz: number) => void): Promise<void> {
    this.onTeleport = onTeleport;
    const res = await fetch(svgUrl);
    if (!res.ok) throw new Error(`Failed to load floor plan: ${res.statusText}`);
    const text = await res.text();

    this.container.innerHTML = text;
    this.svgEl = this.container.querySelector("svg");

    if (this.svgEl) {
      this.svgEl.style.width = "100%";
      this.svgEl.style.height = "100%";
      this.container.addEventListener("click", this._onClick);
      this._parseLines();
    }

    // Re-append indicator overlay (innerHTML wiped it)
    this.container.appendChild(this.indicatorSvg);
  }

  private _visible = false;

  get visible(): boolean { return this._visible; }

  hide(): void {
    this._visible = false;
    this.container.style.display = "none";
  }

  show(): void {
    if (this.svgEl) {
      this._visible = true;
      this.container.style.display = "flex";
    }
  }

  /** Update minimap rotation to match the camera/model orientation. */
  setRotation(radians: number): void {
    this.currentRotation = radians;
    if (this.svgEl) {
      this.svgEl.style.transform = `rotate(${radians}rad)`;
    }
    this.indicatorSvg.style.transform = `rotate(${radians}rad)`;
  }

  /**
   * Update the camera indicator position and direction on the minimap.
   * @param nx  Normalised X position in unrotated SVG space [0..1]
   * @param nz  Normalised Z position in unrotated SVG space [0..1]
   * @param angleRad  Clockwise rotation of the triangle in radians (0 = pointing up)
   */
  setCameraIndicator(nx: number, nz: number, angleRad: number): void {
    const px = nx * 200;
    const py = nz * 200;
    const deg = angleRad * (180 / Math.PI);
    this.indicatorPoly.setAttribute("transform", `translate(${px} ${py}) rotate(${deg})`);
  }

  dispose(): void {
    this.container.removeEventListener("click", this._onClick);
    this.container.remove();
  }

  // ── Parse SVG polylines back to world coordinates ──────────────────────────

  private _parseLines(): void {
    this.lines = [];
    const svg = this.svgEl;
    if (!svg) return;

    const minX = parseFloat(svg.getAttribute("data-min-x") ?? "0");
    const minY = parseFloat(svg.getAttribute("data-min-y") ?? "0");
    const scale = parseFloat(svg.getAttribute("data-scale") ?? "100");
    const padding = parseFloat(svg.getAttribute("data-padding") ?? "0.5");

    this.minX = minX;
    this.minY = minY;
    this.scale = scale;
    this.padding = padding;
    this.svgWidthPx = parseFloat(svg.getAttribute("width") ?? "0");
    this.svgHeightPx = parseFloat(svg.getAttribute("height") ?? "0");

    this.rotation = parseFloat(svg.getAttribute("data-rotation") ?? "0");

    const polylines = svg.querySelectorAll("polyline");
    for (const pl of polylines) {
      const ptsAttr = pl.getAttribute("points");
      if (!ptsAttr) continue;

      const points: Array<{ x: number; y: number }> = [];
      const pairs = ptsAttr.trim().split(/\s+/);
      for (const pair of pairs) {
        const [pxStr, pyStr] = pair.split(",");
        const px = parseFloat(pxStr);
        const py = parseFloat(pyStr);
        // Reverse SVG transform (no Y-flip — plan y == world Z):
        //   px = (x - minX + padding) * scale  →  x = px/scale - padding + minX
        //   py = (z - minY + padding) * scale  →  z = py/scale - padding + minY
        const x = px / scale - padding + minX;
        const y = py / scale - padding + minY;
        points.push({ x, y });
      }
      if (points.length >= 2) {
        this.lines.push({ points });
      }
    }
  }

  // ── Events ──────────────────────────────────────────────────────────────────

  private _onClick = (e: MouseEvent) => {
    if (!this.svgEl) return;
    // Use the container rect (fixed 200×200, never rotated) instead of the
    // SVG's bounding rect which changes shape when CSS-rotated.
    const rect = this.container.getBoundingClientRect();
    // Click position relative to container centre, normalised to [-0.5, 0.5].
    const cx = (e.clientX - rect.left) / rect.width - 0.5;
    const cy = (e.clientY - rect.top) / rect.height - 0.5;
    // Inverse-rotate to get coordinates in the original (unrotated) SVG space.
    const cos = Math.cos(-this.currentRotation);
    const sin = Math.sin(-this.currentRotation);
    const nx = cx * cos - cy * sin + 0.5;
    const nz = cx * sin + cy * cos + 0.5;

    if (this.clickMode === "align") {
      const [x, z] = this.normalizedToPlanCoords(nx, nz);
      this.onAlignPick?.(x, z);
    } else {
      this.onTeleport?.(nx, nz);
    }
  };

  /**
   * Convert normalised SVG-space coords (click space) back to absolute
   * RoomPlan 2D coords (metres, XZ plane). Mirrors the inverse used by
   * `_parseLines` for polyline points.
   */
  normalizedToPlanCoords(nx: number, nz: number): [number, number] {
    const px = nx * this.svgWidthPx;
    const py = nz * this.svgHeightPx;
    const x = px / this.scale - this.padding + this.minX;
    const z = py / this.scale - this.padding + this.minY;
    return [x, z];
  }
}
